/**
 * The trees report's figures from a forest summary's `tree_stats.json` instead of from every
 * crown: the photo inventory already counted, summed and binned by genus over the whole area
 * (forestum `forest_summary`, spec "tree_stats.json version 1"). Everything the sections show is
 * derived here; what is binned is exact to the bin (0.5 m height, 1 cm DBH, 0.05 m³ volume).
 */
import { genusOf } from '@/map/layers';
import type { Threshold } from './config';
import type { Area } from './geometry';
import { FALLBACK_MIN_DBH, thresholdsFor, type MaturityRow, type MaturityStats } from './maturity';
import { DBH_CLASS_EDGES, HISTOGRAM_BINS, NON_SPECIES, OTHER, stratumOf, type BaseStats, type Derived, type SpeciesRow, type Stacked, type StackedRow, type Strata } from './stats';
import type { Metric, Tree } from './types';

export const TREE_STATS_FILE = 'tree_stats.json';
/** The unit tree_stats.json gives the whole area as. */
export const WHOLE_AREA = 'area';

export interface Hist {
  start: number;
  counts: number[];
}
interface Metric1 {
  n: number;
  sum?: number;
  min?: number;
  max?: number;
  hist?: Hist;
}
export interface GenusStats {
  count: number;
  volume: number;
  height?: Metric1;
  dbh_cm?: Metric1 & { volume_by_bin?: number[] };
  volume_m3?: Metric1;
}
export interface UnitStats {
  area_ha: number | null;
  covered_ha: number | null;
  count: number;
  genera: Record<string, GenusStats>;
}
export interface TreeStatsFile {
  bins: { height_m: number; dbh_cm: number; volume_m3: number };
  units: Record<string, UnitStats>;
}

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const isObject = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);

function metric(v: unknown): Metric1 | undefined {
  if (!isObject(v)) return undefined;
  const h = isObject(v.hist) ? { start: num(v.hist.start) ?? 0, counts: Array.isArray(v.hist.counts) ? v.hist.counts.map((c) => num(c) ?? 0) : [] } : undefined;
  return { n: num(v.n) ?? 0, sum: num(v.sum) ?? undefined, min: num(v.min) ?? undefined, max: num(v.max) ?? undefined, hist: h };
}

/** Read leniently; null when the file is not a tree_stats.json version 1. */
export function parseTreeStats(raw: unknown): TreeStatsFile | null {
  if (!isObject(raw) || raw.version !== 1 || !isObject(raw.units) || !isObject(raw.bins)) return null;
  const bins = { height_m: num(raw.bins.height_m) ?? 0.5, dbh_cm: num(raw.bins.dbh_cm) ?? 1, volume_m3: num(raw.bins.volume_m3) ?? 0.05 };
  const units: Record<string, UnitStats> = {};
  for (const [id, u] of Object.entries(raw.units)) {
    if (!isObject(u)) continue;
    const genera: Record<string, GenusStats> = {};
    for (const [g, s] of Object.entries(isObject(u.genera) ? u.genera : {})) {
      if (!isObject(s)) continue;
      const dbh = metric(s.dbh_cm);
      const byBin = isObject(s.dbh_cm) && Array.isArray(s.dbh_cm.volume_by_bin) ? s.dbh_cm.volume_by_bin.map((x) => num(x) ?? 0) : undefined;
      genera[g] = {
        count: num(s.count) ?? 0,
        // The spec names both the genus' total and its per-tree statistics `volume`; the total is
        // read from either (the object's `sum`), the statistics from `volume_m3` or the object.
        volume: num(s.volume) ?? (isObject(s.volume) ? (num(s.volume.sum) ?? 0) : 0),
        height: metric(s.height),
        dbh_cm: dbh ? { ...dbh, volume_by_bin: byBin } : undefined,
        volume_m3: metric(s.volume_m3 ?? (isObject(s.volume) ? s.volume : undefined)),
      };
    }
    units[id] = { area_ha: num(u.area_ha), covered_ha: num(u.covered_ha), count: num(u.count) ?? 0, genera };
  }
  return { bins, units };
}

/** A genus by its palette key; the file's spellings of one genus (`unknown`, `Unknown`) merged. */
function byKey(unit: UnitStats): Map<string, GenusStats[]> {
  const out = new Map<string, GenusStats[]>();
  for (const [g, s] of Object.entries(unit.genera)) {
    const key = genusOf(g) || 'Unknown';
    out.set(key, [...(out.get(key) ?? []), s]);
  }
  return out;
}

const sumOf = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
const meanOf = (list: (Metric1 | undefined)[]): number | null => {
  const n = sumOf(list.map((m) => m?.n ?? 0));
  return n ? sumOf(list.map((m) => m?.sum ?? 0)) / n : null;
};

/** Each non-empty fine bin, as its centre and count, for one metric of some genera. */
function bins(list: (Metric1 | undefined)[], width: number): { centre: number; lower: number; count: number; index: number; from: Metric1 }[] {
  const out: { centre: number; lower: number; count: number; index: number; from: Metric1 }[] = [];
  for (const m of list) {
    const h = m?.hist;
    if (!h) continue;
    h.counts.forEach((count, i) => {
      if (count > 0) out.push({ centre: h.start + (i + 0.5) * width, lower: h.start + i * width, count, index: i, from: m });
    });
  }
  return out;
}

function extentOf(list: (Metric1 | undefined)[]): [number, number] | null {
  const mins = list.map((m) => m?.min).filter((v): v is number => v !== undefined);
  const maxs = list.map((m) => m?.max).filter((v): v is number => v !== undefined);
  return mins.length && maxs.length ? [Math.min(...mins), Math.max(...maxs)] : null;
}

const STACK_SPECIES = 8;

/** Fine bins into the page's stacked bars: `place` names the row of a fine bin's centre, or null to drop it. */
function restack(groups: Map<string, GenusStats[]>, rows: StackedRow[], pick: (s: GenusStats) => Metric1 | undefined, width: number, place: (centre: number) => number | null): Stacked {
  const top = [...groups.entries()]
    .map(([g, list]) => [g, sumOf(list.map((s) => s.count))] as const)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, STACK_SPECIES)
    .map(([g]) => g);
  let other = false;
  for (const [genus, list] of groups) {
    const key = top.includes(genus) ? genus : OTHER;
    for (const b of bins(list.map(pick), width)) {
      const i = place(b.centre);
      if (i === null) continue;
      if (key === OTHER) other = true;
      rows[i]!.counts[key] = (rows[i]!.counts[key] ?? 0) + b.count;
    }
  }
  return { series: other ? [...top, OTHER] : top, rows };
}

/** The page's histogram (equal bins over the positive values, as from every tree), re-binned from the fine one. */
function histogramOf(groups: Map<string, GenusStats[]>, pick: (s: GenusStats) => Metric1 | undefined, width: number, count: number): Stacked {
  const all = [...groups.values()].flat().map(pick);
  const fine = bins(all, width).filter((b) => b.centre > 0);
  if (!fine.length) return { series: [], rows: [] };
  const range = extentOf(all);
  // As from every tree: from the smallest positive value (the first positive bin's edge if a 0 is in the data).
  const min = range && range[0] > 0 ? range[0] : Math.max(0, Math.min(...fine.map((b) => b.lower)));
  const max = range?.[1] ?? Math.max(...fine.map((b) => b.lower + width));
  const w = (max - min) / count || 1;
  const rows = Array.from({ length: count }, (_, i) => ({ from: min + i * w, to: i === count - 1 ? max : min + (i + 1) * w, counts: {} }));
  return restack(groups, rows, pick, width, (c) => (c <= 0 ? null : Math.max(0, Math.min(count - 1, Math.floor((c - min) / w)))));
}

/** Derived figures of one unit, plus the points the maps draw (from the crowns' tile archive). */
export function summaryStats(file: TreeStatsFile, points: Tree[], unitId = WHOLE_AREA, sample = 1): BaseStats | null {
  const unit = file.units[unitId];
  if (!unit) return null;
  const groups = byKey(unit);
  const all = [...groups.values()].flat();
  const count = sumOf(all.map((s) => s.count));
  const volume = sumOf(all.map((s) => Math.abs(s.volume)));
  const area: Area = { ha: unit.area_ha ?? 0, source: 'summary' };
  const perHa = (v: number) => (area.ha > 0 ? v / area.ha : null);
  const row = (genus: string, list: GenusStats[]): SpeciesRow => {
    const c = sumOf(list.map((s) => s.count));
    const v = sumOf(list.map((s) => Math.abs(s.volume)));
    return {
      genus,
      isSpecies: !NON_SPECIES.has(genus),
      count: c,
      countPct: count ? (c / count) * 100 : 0,
      volume: v,
      volumePct: volume > 0 ? (v / volume) * 100 : null,
      meanHeight: meanOf(list.map((s) => s.height)),
      meanDbh: meanOf(list.map((s) => s.dbh_cm)),
      volumePerHa: perHa(v),
    };
  };
  const rows = [...groups.entries()].map(([g, list]) => row(g, list)).sort((a, b) => b.count - a.count || a.genus.localeCompare(b.genus));
  const total = row('', all);

  // Height layers: thirds of the height range, the trees of each fine bin by its centre.
  let strata: Strata | null = null;
  const hr = extentOf(all.map((s) => s.height));
  if (hr) {
    const [min, max] = hr;
    strata = { min, max, lo: min + (max - min) / 3, hi: min + (2 * (max - min)) / 3, counts: { lower: 0, middle: 0, upper: 0 } };
    for (const b of bins(all.map((s) => s.height), file.bins.height_m)) strata.counts[stratumOf(Math.min(max, Math.max(min, b.centre)), strata)] += b.count;
  }

  const pick: Record<Metric, (s: GenusStats) => Metric1 | undefined> = { height: (s) => s.height, dbh: (s) => s.dbh_cm, volume: (s) => s.volume_m3 };
  const width: Record<Metric, number> = { height: file.bins.height_m, dbh: file.bins.dbh_cm, volume: file.bins.volume_m3 };
  const histograms = Object.fromEntries((['height', 'dbh', 'volume'] as Metric[]).map((m) => [m, histogramOf(groups, pick[m], width[m], HISTOGRAM_BINS[m])])) as Record<Metric, Stacked>;
  const ranges = Object.fromEntries((['height', 'dbh', 'volume'] as Metric[]).map((m) => [m, extentOf(all.map(pick[m]))])) as Record<Metric, [number, number] | null>;
  // The page's classes (stats.ts DBH_CLASS_EDGES): <10, 10–20, …, 50+, each a whole number of 1 cm bins.
  const edges = DBH_CLASS_EDGES;
  const classRows = edges.map((from, i) => ({ from, to: edges[i + 1] ?? Infinity, counts: {} }));
  const dbhClasses = restack(groups, classRows, pick.dbh, width.dbh, (c) => (c < 0 ? null : Math.min(edges.length - 1, Math.floor(c / 10))));

  const derived: Derived = {
    histograms,
    dbhClasses,
    ranges,
    sample,
    maturity: (overrides: Threshold[]) => maturityOf(groups, count, width.dbh, overrides),
  };
  return {
    trees: points,
    area,
    summary: { count, volume, volumePerHa: perHa(volume), meanDbh: meanOf(all.map((s) => s.dbh_cm)), meanHeight: meanOf(all.map((s) => s.height)) },
    species: { rows, total },
    strata,
    derived,
  };
}

/** Maturity from the DBH histograms: a tree is mature from its genus' target; its volume from `volume_by_bin`. */
export function maturityOf(groups: Map<string, GenusStats[]>, allCount: number, width: number, overrides: Threshold[]): MaturityStats {
  const thresholds = thresholdsFor(overrides);
  const overridden = new Set(overrides.map((o) => genusOf(o.genus)));
  let matureCount = 0;
  let matureVolume = 0;
  const rows: MaturityRow[] = [];
  for (const [genus, list] of groups) {
    if (NON_SPECIES.has(genus)) continue;
    const target = thresholds[genus] ?? null;
    const min = target?.min ?? FALLBACK_MIN_DBH;
    let mature = 0;
    for (const s of list) {
      const h = s.dbh_cm?.hist;
      if (!h) continue;
      h.counts.forEach((c, i) => {
        // A bin counts from the threshold on: exact for whole-centimetre targets on 1 cm bins.
        if (h.start + i * width >= min - 1e-9) {
          mature += c;
          matureVolume += s.dbh_cm?.volume_by_bin?.[i] ?? 0;
        }
      });
    }
    matureCount += mature;
    const total = sumOf(list.map((s) => s.count));
    rows.push({ genus, total, mature, pct: total ? (mature / total) * 100 : 0, meanDbh: meanOf(list.map((s) => s.dbh_cm)), target, overridden: overridden.has(genus) });
  }
  rows.sort((a, b) => b.total - a.total || a.genus.localeCompare(b.genus));
  return { matureCount, maturePct: allCount ? (matureCount / allCount) * 100 : 0, matureVolume, rows };
}
